import {
	TreatmentConfigTemplate,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";

/**
 * SIMS-9231 AC4/AC5 (portal Q-3 = B; Hi-Fi v2 Screen 2B, badge 17) — tell the administrator, at
 * save, when a disenrollment threshold they just set on a segment will not take effect.
 *
 * The nightly job holds a participant on a plan with several activities to the MOST PERMISSIVE
 * limit across them (AC2). So a segment override shorter than a longer threshold configured
 * elsewhere is inert while that longer one applies. The editor knows no participant, so it cannot
 * say which limit is in force; what it can say is the longest CONFIGURED, an upper bound. The
 * wording must not claim more than that.
 *
 * Decisions (recorded on SIMS-9231):
 * - "Configured" is every explicit value: the template's and each activity's own default, and
 *   every segment override that is switched on. The 30-day fallback is not configured.
 * - Scope is one template with its activities: the set a participant on that template is
 *   evaluated across. A value on another template never applies to the same participant.
 * - Only a segment override that is new or changed in this edit is reported ("the threshold I
 *   set"); an inert value saved earlier is not repeated on every save.
 * - Not shorter, not inert: a value equal to the longest gets no message (C19834).
 * - Two values set in the same edit: the longer of them is configured too, so it can be the one
 *   named for the shorter (and, being the longest, gets no notice itself).
 *
 * Pure; the form draws what it returns. Synthetic data only — no PHI.
 */

/** Where a configured threshold lives, and what the message calls it. */
export interface ConfiguredThreshold {
	value: number;
	label: string;
	/** Present for a segment override: the activity (undefined = the template) and segment index. */
	activityIndex?: number;
	segmentIndex?: number;
}

/** Notices per segment index, for the template's editor and each activity's own editor. */
export interface InertThresholdNotices {
	template: Record<number, string>;
	activities: Record<number, Record<number, string>>;
}

export const NO_INERT_NOTICES: InertThresholdNotices = {
	template: {},
	activities: {},
};

const isSet = (value: unknown): value is number =>
	typeof value === "number" && Number.isFinite(value) && value > 0;

const activityLabel = (
	activity: TreatmentConfigTemplate,
	index: number,
): string => activity.name?.trim() || `Additional Activity ${index + 1}`;

const segmentOverrides = (
	segments: TreatmentSegment[] | undefined,
	label: string,
	activityIndex: number | undefined,
): ConfiguredThreshold[] =>
	(segments ?? []).flatMap((segment, segmentIndex) =>
		isSet(segment.disenrollmentDaysThreshold)
			? [
					{
						value: segment.disenrollmentDaysThreshold,
						label: `${label} · Segment ${segmentIndex + 1}`,
						activityIndex,
						segmentIndex,
					},
				]
			: [],
	);

/** Every explicit threshold on a template and its activities, in the order they are drawn. */
export function configuredThresholds(
	template: TreatmentConfigTemplate,
): ConfiguredThreshold[] {
	const name = template.name?.trim() || "Template";
	const found: ConfiguredThreshold[] = [];
	if (isSet(template.disenrollmentDaysThreshold)) {
		found.push({
			value: template.disenrollmentDaysThreshold,
			label: `${name} default`,
		});
	}
	found.push(...segmentOverrides(template.segments, name, undefined));
	(template.additionalTreatmentConfigs ?? []).forEach((activity, index) => {
		const label = activityLabel(activity, index);
		if (isSet(activity.disenrollmentDaysThreshold)) {
			found.push({
				value: activity.disenrollmentDaysThreshold,
				label: `${label} default`,
			});
		}
		found.push(...segmentOverrides(activity.segments, label, index));
	});
	return found;
}

/** The PO's sentence (HANDOFF "Inert threshold" string). */
export function inertThresholdMessage(
	longest: ConfiguredThreshold,
	value: number,
): string {
	return (
		"Disenrollment is evaluated across all activities and the most permissive threshold " +
		`applies. The longest configured on this plan is ${longest.value} days (${longest.label}), ` +
		`so this ${value} will not take effect.`
	);
}

const segmentsOf = (
	template: TreatmentConfigTemplate | undefined,
	activityIndex: number | undefined,
): TreatmentSegment[] | undefined =>
	activityIndex === undefined
		? template?.segments
		: template?.additionalTreatmentConfigs?.[activityIndex]?.segments;

/**
 * The notices for one saved template: every segment override new or changed since `before` that
 * is shorter than the longest threshold configured elsewhere on the template and its activities.
 */
export function inertThresholdNotices(
	before: TreatmentConfigTemplate | undefined,
	after: TreatmentConfigTemplate,
): InertThresholdNotices {
	const notices: InertThresholdNotices = { template: {}, activities: {} };
	const all = configuredThresholds(after);
	all.forEach((candidate, position) => {
		if (candidate.segmentIndex === undefined) return;
		const previous = segmentsOf(before, candidate.activityIndex)?.[
			candidate.segmentIndex
		]?.disenrollmentDaysThreshold;
		if (previous === candidate.value) return;
		const longest = all
			.filter((_, other) => other !== position)
			.reduce<
				ConfiguredThreshold | undefined
			>((max, next) => (!max || next.value > max.value ? next : max), undefined);
		if (!longest || candidate.value >= longest.value) return;
		const message = inertThresholdMessage(longest, candidate.value);
		if (candidate.activityIndex === undefined) {
			notices.template[candidate.segmentIndex] = message;
		} else {
			notices.activities[candidate.activityIndex] = {
				...notices.activities[candidate.activityIndex],
				[candidate.segmentIndex]: message,
			};
		}
	});
	return notices;
}

export const hasInertNotices = (notices: InertThresholdNotices): boolean =>
	Object.keys(notices.template).length > 0 ||
	Object.values(notices.activities).some(
		(segments) => Object.keys(segments).length > 0,
	);

/**
 * The saved template's counterpart before the edit: matched by its stable `_id`, else by position
 * (a template saved before ids existed, or one added in this edit, which has none before).
 */
export function templateBefore(
	beforeTemplates: TreatmentConfigTemplate[] | undefined,
	after: TreatmentConfigTemplate,
	index: number,
): TreatmentConfigTemplate | undefined {
	const id = (after as { _id?: string })._id;
	if (id) {
		return (beforeTemplates ?? []).find(
			(template) => (template as { _id?: string })._id === id,
		);
	}
	return beforeTemplates?.[index];
}
